import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { PostHogIntegration } from '@src/monitoring/utils/PostHogIntegration';
import { analytics } from '@src/utility/analytics';
import { LogManager } from '@src/utility/Logger';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';

import type { UserResource } from '@clerk/types';

const logger = LogManager.getLogger('middleware:analytics');

interface AnalyticsUser {
  id: string;
  email: string | undefined;
  name: string;
  type: 'external';
  source: 'clerk';
}

function buildAnalyticsUser(user: UserResource): AnalyticsUser {
  const name =
    (user.fullName ??
      [user.firstName, user.lastName].filter(Boolean).join(' ')) ||
    'Unknown';

  return {
    id: user.id,
    email: user.primaryEmailAddress?.emailAddress,
    name,
    type: 'external',
    source: 'clerk',
  };
}

function identifyUser(analyticsUser: AnalyticsUser): void {
  logger.info('Identifying user:', analyticsUser);

  const posthogApiKey = process.env.REACT_APP_POSTHOG_API_KEY;
  if (posthogApiKey) {
    PostHogIntegration.init(posthogApiKey, {
      service: 'arbor-video',
      env:
        process.env.DATO_ENV ||
        process.env.REACT_APP_DATO_ENV ||
        process.env.NODE_ENV ||
        'dev',
      version:
        process.env.REACT_APP_PACKAGE_VERSION ??
        process.env.REACT_APP_BUILD_COMMIT_SHA ??
        undefined,
    });
  }

  PostHogIntegration.identify(analyticsUser.id, {
    user_id: analyticsUser.id,
    display_name: analyticsUser.name,
    ...(analyticsUser.email ? { email: analyticsUser.email } : {}),
  });
  // Diagnoses sessions missing from Datadog: an empty id means RUM isn't
  // tracking this page load (the SDK's own warnings bypass PostHog's console
  // capture). The id is as of identify; a later Datadog renewal isn't reflected.
  PostHogIntegration.setContext({
    dd_session_id: datadogRum.getInternalContext()?.session_id ?? null,
    dd_cookie_present: document.cookie.includes('_dd_s='),
    dd_cookie: document.cookie.match(/(?:^|;\s*)_dd_s=([^;]*)/)?.[1] ?? null,
  });
  posthog.capture('$pageview');

  datadogRum.setUser(analyticsUser);
  datadogLogs.setUser(analyticsUser);
  analytics.identify(analyticsUser.id, analyticsUser);
}

export function useAnalyticsIdentification({
  clerkUser,
  isClerkLoaded,
}: {
  clerkUser: UserResource | null | undefined;
  isClerkLoaded: boolean;
}): void {
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isClerkLoaded || !clerkUser) return;

    // Already identified this user
    if (identifiedUserIdRef.current === clerkUser.id) return;

    const analyticsUser = buildAnalyticsUser(clerkUser);
    identifyUser(analyticsUser);
    identifiedUserIdRef.current = clerkUser.id;
  }, [clerkUser, isClerkLoaded]);
}
